import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import axios from "axios";
import {
  Modal,
  Button,
  Form,
  Container,
  Row,
  Col,
  Card,
  Spinner,
  Alert,
  InputGroup,
} from "react-bootstrap";
import Sidebar from "../../components/Sidebar";
import Topbar from "../../components/Topbar";
import { FaLocationArrow } from "react-icons/fa6";
import { toast } from "react-toastify";

// --- Google Maps Script Loader Hook ---
const useGoogleMapsScript = (apiKey) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (window.google && window.google.maps && window.google.maps.places) {
      setIsLoaded(true);
      return;
    }
    const scriptId = "google-maps-api-script";
    if (document.getElementById(scriptId)) {
      const interval = setInterval(() => {
        if (window.google && window.google.maps && window.google.maps.places) {
          setIsLoaded(true);
          clearInterval(interval);
        }
      }, 100);
      return;
    }

    const script = document.createElement("script");
    script.id = scriptId;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places,geocoding`;
    script.async = true;
    script.defer = true;
    script.onload = () => setIsLoaded(true);
    script.onerror = () =>
      setError(new Error("Failed to load Google Maps script."));
    document.body.appendChild(script);
  }, [apiKey]);

  return { isLoaded, error };
};

// --- Reusable Map Component ---
const MapDisplay = ({ center, zoom, markerPosition, onClick, style }) => {
  const mapRef = useRef(null);
  const [map, setMap] = useState(null);
  const [marker, setMarker] = useState(null);

  useEffect(() => {
    if (mapRef.current && window.google && !map) {
      const newMap = new window.google.maps.Map(mapRef.current, {
        center: center,
        zoom: zoom,
        disableDefaultUI: true,
      });
      setMap(newMap);
    }
  }, [mapRef, map, center, zoom]);

  useEffect(() => {
    if (map && onClick) {
      const listener = map.addListener("click", onClick);
      return () => window.google.maps.event.removeListener(listener);
    }
  }, [map, onClick]);

  useEffect(() => {
    if (map) {
      if (!marker && markerPosition) {
        setMarker(
          new window.google.maps.Marker({ position: markerPosition, map: map })
        );
      } else if (marker) {
        if (markerPosition) {
          marker.setPosition(markerPosition);
          marker.setMap(map);
        } else {
          marker.setMap(null);
        }
      }
    }
  }, [map, marker, markerPosition]);

  useEffect(() => {
    if (map && center) {
      map.panTo(center);
    }
  }, [map, center]);

  return <div ref={mapRef} style={style} />;
};

const ShopLocation = () => {
  const BASE_URL = process.env.REACT_APP_APIURL;
  const Maps_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";

  const searchInputRef = useRef(null);
  const autocompleteRef = useRef(null);

  // --- State Management ---
  const [shopData, setShopData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [cityInput, setCityInput] = useState("");
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDetecting, setIsDetecting] = useState(false);

  // --- Script Loading ---
  const { isLoaded: isMapsScriptLoaded, error: mapsScriptError } =
    useGoogleMapsScript(Maps_API_KEY);

  // --- Setup Google Places Autocomplete ---
  useEffect(() => {
    // This effect runs when the modal is opened and the script is loaded
    if (isMapsScriptLoaded && showModal && searchInputRef.current) {
      autocompleteRef.current = new window.google.maps.places.Autocomplete(
        searchInputRef.current,
        { fields: ["geometry", "name", "formatted_address"] }
      );

      autocompleteRef.current.addListener("place_changed", () => {
        const place = autocompleteRef.current.getPlace();
        if (place && place.geometry && place.geometry.location) {
          const newLocation = {
            latitude: place.geometry.location.lat(),
            longitude: place.geometry.location.lng(),
          };
          // UPDATED: Use formatted_address for a more complete address
          setCityInput(place.formatted_address || place.name || "");
          setSelectedLocation(newLocation);
        }
      });
    }
  }, [isMapsScriptLoaded, showModal]);

  // --- Data Fetching ---
  const fetchShopData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await axios.get(`${BASE_URL}api/shop`);
      if (response.data && response.data.success) {
        setShopData(response.data.shopData);
      } else {
        throw new Error(response.data.message || "Failed to fetch shop data.");
      }
    } catch (err) {
      setError(err.message || "An error occurred while fetching data.");
      console.error("Fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, [BASE_URL]);

  useEffect(() => {
    fetchShopData();
  }, [fetchShopData]);

  // --- Update Handler ---
  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!cityInput || !selectedLocation) {
      alert("Please provide a city and select a location on the map.");
      return;
    }
    setIsUpdating(true);
    setError("");
    try {
      const payload = { city: cityInput, location: selectedLocation };
      const response = await axios.put(`${BASE_URL}api/shop`, payload);

      if (response.data && response.data.success) {
        await fetchShopData();
        setShowModal(false);
      } else {
        throw new Error(response.data.message || "Failed to update shop data.");
      }
    } catch (err) {
      setError(err.message || "An error occurred while updating.");
      console.error("Update error:", err);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleModalOpen = () => {
    if (shopData) {
      setCityInput(shopData.city);
      setSelectedLocation(shopData.location);
      setShowModal(true);
    }
  };

  // --- Auto-detect location handler ---
  const handleDetectLocation = () => {
    if (!navigator.geolocation) {
      alert("Geolocation is not supported by your browser.");
      return;
    }
    setIsDetecting(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        const newLocation = { latitude, longitude };
        setSelectedLocation(newLocation);

        // Reverse geocode to find city name
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode(
          { location: { lat: latitude, lng: longitude } },
          (results, status) => {
            setIsDetecting(false);
            if (status === "OK" && results[0]) {
              // Find the city component from the address
              const addressComponents = results[0].address_components;
              const cityComponent = addressComponents.find(
                (c) =>
                  c.types.includes("locality") ||
                  c.types.includes("postal_town")
              );
              setCityInput(
                cityComponent
                  ? cityComponent.long_name
                  : results[0].formatted_address
              );
            } else {
              console.error("Geocoder failed due to: " + status);
              setCityInput("Unknown Location");
            }
          }
        );
      },
      (err) => {
        setIsDetecting(false);
        alert(`Error detecting location: ${err.message}`);
      }
    );
  };

  // --- Map Helper Logic ---
  const mapCenter = useMemo(
    () =>
      shopData
        ? { lat: shopData.location.latitude, lng: shopData.location.longitude }
        : { lat: 22.5726, lng: 88.3639 },
    [shopData]
  );
  const modalMapCenter = useMemo(
    () =>
      selectedLocation
        ? { lat: selectedLocation.latitude, lng: selectedLocation.longitude }
        : mapCenter,
    [selectedLocation, mapCenter]
  );
  const modalMarkerPosition = useMemo(
    () =>
      selectedLocation
        ? { lat: selectedLocation.latitude, lng: selectedLocation.longitude }
        : null,
    [selectedLocation]
  );
  const mapContainerStyle = {
    height: "400px",
    width: "100%",
    borderRadius: "0.375rem",
    backgroundColor: "#e9ecef",
    position: "relative",
  };

  return (
    <div>
      {/* ADDED: This style tag fixes the search dropdown visibility issue inside the modal */}
      <style>{`.pac-container { z-index: 1056 !important; }`}</style>

      <Topbar />
      <Sidebar />

      <main id="main">
        <Container fluid>
          {!Maps_API_KEY && (
            <Alert variant="danger">
              <h4>Configuration Error</h4>
              <p>
                Google Maps API key is not provided. Please set{" "}
                <code>REACT_APP_Maps_API_KEY</code>.
              </p>
            </Alert>
          )}
          {loading && (
            <div className="text-center my-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-2">Loading Data...</p>
            </div>
          )}
          {!loading && (error || mapsScriptError) && (
            <Alert variant="danger">
              {error || (mapsScriptError && mapsScriptError.message)}
            </Alert>
          )}

          {!loading &&
            !error &&
            !mapsScriptError &&
            shopData &&
            isMapsScriptLoaded && (
              <>
                {/* Shop Status Card */}
                <Row className="mb-4">
                  <Col>
                    <Card className="shadow">
                      <Card.Header className="py-3 d-flex flex-row align-items-center justify-content-between">
                        <h6 className="m-0 font-weight-bold text-primary">
                          Shop Status
                        </h6>
                        <Form.Check
                          type="switch"
                          id="shop-status-switch"
                          label={shopData.isOpen ? "OPEN" : "CLOSED"}
                          checked={shopData.isOpen ?? true}
                          onChange={async (e) => {
                            const newStatus = e.target.checked;
                            // Optimistic update
                            const oldStatus = shopData.isOpen;
                            setShopData((prev) => ({ ...prev, isOpen: newStatus }));

                            try {
                              const res = await axios.put(`${BASE_URL}api/shop`, {
                                isOpen: newStatus,
                              });
                              if (res.data.success) {
                                toast.success(
                                  `Shop is now ${newStatus ? "OPEN" : "CLOSED"}`
                                );
                              } else {
                                throw new Error(res.data.message);
                              }
                            } catch (error) {
                              console.error("Status update error", error);
                              toast.error("Failed to update status");
                              // Revert
                              setShopData((prev) => ({ ...prev, isOpen: oldStatus }));
                            }
                          }}
                          style={{ transform: "scale(1.2)", fontWeight: "bold" }}
                        />
                      </Card.Header>
                      <Card.Body>
                        <div className="d-flex align-items-center">
                          <div
                            className={`rounded-circle me-3 ${shopData.isOpen ? "bg-success" : "bg-danger"
                              }`}
                            style={{ width: "15px", height: "15px" }}
                          ></div>
                          <div>
                            <h5 className="mb-1">
                              The shop is currently{" "}
                              <span
                                className={
                                  shopData.isOpen ? "text-success" : "text-danger"
                                }
                              >
                                {shopData.isOpen ? "Accepting Orders" : "Closed"}
                              </span>
                            </h5>
                            <p className="text-muted mb-0">
                              {shopData.isOpen
                                ? "Customers can browse products and place orders normally."
                                : "The shop is closed. Customers will see a 'Shop Closed' overlay and cannot place orders."}
                            </p>
                          </div>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>

                <Row>
                  <Col>
                    <Card className="shadow mb-4">
                      <Card.Header className="py-3 d-flex flex-row align-items-center justify-content-between">
                        <h6 className="m-0 font-weight-bold text-primary">
                          Current Shop Location
                        </h6>
                        <Button variant="primary" onClick={handleModalOpen}>
                          <i className="fas fa-edit me-2"></i>Update
                        </Button>
                      </Card.Header>
                      <Card.Body>
                        <Row>
                          <Col md={4}>
                            <h4>{shopData.city}</h4>
                            <p className="mb-1">
                              <strong>Latitude:</strong>{" "}
                              {shopData.location.latitude}
                            </p>
                            <p>
                              <strong>Longitude:</strong>{" "}
                              {shopData.location.longitude}
                            </p>
                          </Col>
                          <Col md={8}>
                            <MapDisplay
                              center={mapCenter}
                              zoom={14}
                              markerPosition={mapCenter}
                              style={mapContainerStyle}
                            />
                          </Col>
                        </Row>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>

                <Modal
                  show={showModal}
                  onHide={() => setShowModal(false)}
                  size="lg"
                  centered
                >
                  <Form onSubmit={handleUpdate}>
                    <Modal.Header closeButton>
                      <Modal.Title>Update Shop Location</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                      {error && <Alert variant="danger">{error}</Alert>}
                      <Form.Group className="mb-3">
                        <Form.Label>City Name</Form.Label>
                        <InputGroup>
                          <Form.Control
                            type="text"
                            value={cityInput}
                            onChange={(e) => setCityInput(e.target.value)}
                            required
                          />
                          <Button
                            variant="outline-secondary"
                            onClick={handleDetectLocation}
                            disabled={isDetecting}
                          >
                            {isDetecting ? (
                              <Spinner as="span" size="sm" />
                            ) : (
                              <div>
                                Auto <FaLocationArrow />
                              </div>
                            )}
                          </Button>
                        </InputGroup>
                      </Form.Group>

                      <Form.Group
                        className="mb-3"
                        style={{ position: "relative" }}
                      >
                        <Form.Label>Search or Select New Location</Form.Label>
                        <InputGroup
                          style={{
                            position: "absolute",
                            top: "35px",
                            left: "10px",
                            width: "300px",
                            zIndex: 10,
                          }}
                        >
                          <Form.Control
                            ref={searchInputRef}
                            placeholder="Search for a location..."
                          />
                        </InputGroup>
                        <MapDisplay
                          center={modalMapCenter}
                          zoom={14}
                          onClick={(e) =>
                            setSelectedLocation({
                              latitude: e.latLng.lat(),
                              longitude: e.latLng.lng(),
                            })
                          }
                          markerPosition={modalMarkerPosition}
                          style={mapContainerStyle}
                        />
                      </Form.Group>

                      {selectedLocation && (
                        <div className="text-center mt-3">
                          <p className="mb-0">
                            <strong>Selected Latitude:</strong>{" "}
                            {selectedLocation.latitude.toFixed(6)}
                          </p>
                          <p>
                            <strong>Selected Longitude:</strong>{" "}
                            {selectedLocation.longitude.toFixed(6)}
                          </p>
                        </div>
                      )}
                    </Modal.Body>
                    <Modal.Footer>
                      <Button
                        variant="secondary"
                        onClick={() => setShowModal(false)}
                        disabled={isUpdating}
                      >
                        Close
                      </Button>
                      <Button
                        variant="primary"
                        type="submit"
                        disabled={isUpdating}
                      >
                        {isUpdating ? (
                          <>
                            <Spinner as="span" size="sm" /> Saving...
                          </>
                        ) : (
                          "Save Changes"
                        )}
                      </Button>
                    </Modal.Footer>
                  </Form>
                </Modal>
              </>
            )}
          {!loading && !shopData && !error && (
            <Alert variant="warning">No shop data found.</Alert>
          )}
        </Container>
      </main>
    </div>
  );
};

export default ShopLocation;
